import axios from 'axios';
import { readToken, writeAuth, clearAuth } from '../auth/storage';
import { extractDepts, extractSubject, isTokenExpired } from '../auth/jwt';
import { emitLoggedOut, emitTokenUpdated } from '../auth/events';

const RAW = process.env.REACT_APP_BASE_URL || 'http://localhost:8080/api';
const baseURL = RAW.replace(/\/+$/, '');

export const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = readToken();
    if (token) {
      if (isTokenExpired(token)) {
        clearAuth();
        emitLoggedOut();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
        return Promise.reject(new Error("Session expired. Please log in again."));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    const userId = localStorage.getItem('userId');
    if (userId) {
      config.headers['X-User-ID'] = userId;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

function handleUpdatedToken(response) {
  const updated =
    response?.headers?.['x-updated-token'] ||
    response?.headers?.['X-Updated-Token'];
  if (!updated) return;
  const depts = extractDepts(updated);
  const sub = extractSubject(updated);
  writeAuth({ token: updated, depts, ...(sub ? { username: sub } : {}) });
  emitTokenUpdated({ token: updated, depts });
}

api.interceptors.response.use(
  (response) => {
    handleUpdatedToken(response);
    return response;
  },
  (error) => {
    if (error.response) {
      handleUpdatedToken(error.response);
      const status = error.response.status;
      if (status === 401) {
        clearAuth();
        emitLoggedOut();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
      if (status === 403) {
        const err = new Error(
          error.response?.data?.message || 'Access denied',
        );
        err.name = 'AccessDeniedError';
        err.status = 403;
        err.original = error;
        return Promise.reject(err);
      }
      // For all other HTTP errors, extract the backend message from the envelope
      const body = error.response?.data;
      const backendMessage =
        (body && typeof body === 'object' && body.message) ||
        null;
      if (backendMessage) {
        const err = new Error(backendMessage);
        err.status = status;
        err.errorCode = body?.errorCode;
        err.errors = body?.errors;
        err.original = error;
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  },
);

export function unwrap(response) {
  const body = response?.data;
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed');
      err.status = body.status;
      err.errorCode = body.errorCode;
      err.errors = body.errors;
      throw err;
    }
    return body.data;
  }
  return body;
}

export function extractErrorMessage(error) {
  if (!error) return 'Unknown error';
  // Field-level validation errors
  if (error.errors && Array.isArray(error.errors) && error.errors.length) {
    return error.errors.map((e) => `${e.field}: ${e.message}`).join('\n');
  }
  // Backend structured envelope message (already on the Error object by interceptor)
  if (error.message && error.message !== 'Request failed') return error.message;
  // Fallback: read from raw axios response
  const body = error?.response?.data;
  if (body && typeof body === 'object' && body.message) return body.message;
  return error?.message || 'Request failed';
}

export { baseURL };
export default api;
